/**
 * @file QuotationContext.jsx
 * @description Gestor de estado global para el carrito de cotizaciones y panel drawer.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";

const QuotationContext = createContext();

export function QuotationProvider({ children }) {
  const [selectedItems, setSelectedItems] = useState(() => {
    try {
      const saved = localStorage.getItem("agdysa_basket");
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error("Error al leer localStorage:", error);
      return [];
    }
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const itemsRef = useRef(selectedItems);

  useEffect(() => {
    itemsRef.current = selectedItems;
  }, [selectedItems]);

  useEffect(() => {
    try {
      localStorage.setItem("agdysa_basket", JSON.stringify(selectedItems));
    } catch (error) {
      console.error("Error al escribir en localStorage:", error);
    }
  }, [selectedItems]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!isDrawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setIsDrawerOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [isDrawerOpen]);

  const itemCount = selectedItems.length;

  const showToast = useCallback((message) => {
    setToast(message);
  }, []);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setIsDrawerOpen((prev) => !prev), []);

  const addItem = useCallback((item) => {
    const name = item.name || item.title;
    if (!item?.id || !name) return { ok: false, duplicate: false };

    const normalized = {
      id: item.id,
      name,
      category: item.category || "",
    };

    if (itemsRef.current.some((i) => i.id === normalized.id)) {
      return { ok: false, duplicate: true };
    }

    setSelectedItems((prev) => [...prev, normalized]);
    return { ok: true, duplicate: false };
  }, []);

  const removeItem = useCallback((id) => {
    setSelectedItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clearBasket = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const value = useMemo(
    () => ({
      selectedItems,
      itemCount,
      addItem,
      removeItem,
      clearBasket,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      toast,
      showToast,
    }),
    [
      selectedItems,
      itemCount,
      addItem,
      removeItem,
      clearBasket,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      toast,
      showToast,
    ]
  );

  return <QuotationContext.Provider value={value}>{children}</QuotationContext.Provider>;
}

export const useQuotation = () => {
  const context = useContext(QuotationContext);
  if (!context) {
    throw new Error("useQuotation debe usarse dentro de un QuotationProvider");
  }
  return context;
};
